import React, { useEffect, useState } from 'react';
import {
  Flex,
  Heading,
  Box,
  Button,
  Text,
  Input,
  Grid,
  GridItem,
  Switch,
  Icon,
  Image,
  Link,
  Divider,
} from '@chakra-ui/react';
import { AiOutlineSearch } from 'react-icons/ai';
import SidebarWithHeader from '../../../../components/Admin/SidebarWithHeader';
import ManajemenIdentitasController, {
  useManajemenIdentitasContext,
} from '../../../../controller/admin/landing_page/manajemen_identitas';
import {
  FaInstagramSquare,
  FaTwitterSquare,
  FaYoutubeSquare,
} from 'react-icons/fa';
import { FaSquareFacebook } from 'react-icons/fa6';
import { PiPhoneFill } from 'react-icons/pi';
import Loader from '../../../../components/Loader';
import { useRole } from '../../../../../hooks/useRole';

/**
 * Manajemen Identitas is a component that allows users to manage layout like navbar and footer.
 * @component
 * @returns {JSX.Element} JSX.Element
 * @author Bagas Prasetyadi
 */

interface FileUploadProps {
  onChange?: Function;
  defaultImage: string | null | undefined;
}

const FileUpload: React.FC<FileUploadProps> = ({ onChange, defaultImage }) => {
  const [, setSelectedFile] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setSelectedFile(file || null);

    if (onChange && file) {
      onChange(file);

      // Create a URL for the selected file
      const fileUrl = URL.createObjectURL(file);
      setImageUrl(fileUrl);
    }
  };

  useEffect(() => {
    if (defaultImage) setImageUrl(defaultImage);
  }, [defaultImage]);

  return (
    <Box>
      <Input type="file" accept="image/*" onChange={handleFileChange} mb="2" />

      {imageUrl && (
        <Box mt="2">
          <Text fontWeight="bold">Preview:</Text>
          <Image
            src={imageUrl}
            alt="Preview"
            boxSize="150px"
            objectFit="cover"
          />
        </Box>
      )}
    </Box>
  );
};

const ManajemenIdentitas = () => {
  const { canMutate } = useRole();
  const {
    setManajemenIdentitasState: setState,
    setLogoNavbar,
    setLogoFooterLeft,
    setLogoFooterCenter,
    fetchData,
    postData,
  } = useManajemenIdentitasContext();
  const state = useManajemenIdentitasContext().state;

  const {
    logoNavbarUrl,
    logoFooterLeftUrl,
    logoFooterCenterUrl,
    contact,
    urlFacebook,
    urlInstagram,
    urlTwitter,
    urlYoutube,
    copyright,
    loadingAction,
    loadingData,
    isPreview,
  } = state;

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const PreviewComponent = () => {
    return (
      <Box>
        <Box>
          <Text fontSize="md" mb="2">
            Navbar
          </Text>
          <Box bg="white" display={{ base: 'none', md: 'block' }}>
            <Flex
              justify="space-between"
              align="center"
              alignItems="center"
              h={20}
              px={{ base: 8, md: 12 }}
            >
              {/* Logo */}
              <Flex align="center">
                  <Image src={logoNavbarUrl} height={10} />
              </Flex>
              <Flex align="center" alignItems="center" className="gap-4">
                {/* Navigation Link */}
                <Flex className="gap-5">
                   <Text fontSize="md" display="inline-block">
                    Beranda
                    </Text>

                  <Text fontSize="md" display="inline-block">
                    Pelatihan
                    </Text>
                    
                  <Text fontSize="md" display="inline-block">
                    Klinik Pengetahuan
                  </Text>

                  <Text fontSize="md" display="inline-block">
                    Berbagi Pengetahuan
                  </Text>
                
                </Flex>
              </Flex>
    
              {/* Search */}
              <Flex className="gap-3">
                  <Flex position="relative">
                    <Box
                      position="absolute"
                      insetY={0}
                      left={0}
                      pl={3}
                      display="flex"
                      alignItems="center"
                    >
                      <Icon as={AiOutlineSearch} h={5} w={5} color="gray.500" />
                    </Box>
                      <Input
                        type="text"
                        width="240px"
                        fontSize="md"
                        variant="outline"
                        borderColor="gray.300"
                        borderRadius="md"
                        _hover={{ borderColor: 'gray.500' }}
                        _focus={{
                          ring: '1',
                          ringColor: 'gray.500',
                          borderColor: 'gray.500',
                        }}
                        height={10}
                        pl={8}
                        placeholder="Cari Pelatihan"
                      />
                  </Flex>
    
              
                  <Button
                    type="button"
                    color="white"
                    bg="green.700"
                    _hover={{ bg: 'green.800' }}
                    _active={{ bg: 'green.800' }}
                    fontSize="sm"
                    px={4}
                    rounded="md"
                  >
                    Masuk
                  </Button>
              </Flex>
            </Flex>
          </Box>
        </Box>
        <Box mt={4}>
          <Text fontSize="md" mb={2}>
            Footer
          </Text>
          <Box
            style={{
              background:
                'linear-gradient(83.57deg, #16A34A 68.79%, #E2FF7D 113.39%)',
            }}
            w="full"
            pt={8}
            pb={20}
            px={{ base: 8, md: 12 }}
          >
            <Box
              justifyContent="space-between"
              mt={{ md: 4 }}
              alignItems="center"
              flexDirection={{ base: 'column', md: 'row' }}
              display={{ base: 'none', md: 'flex' }}
            >
              <Box mt={4}>
                <Flex
                  alignItems="center"
                  justifyContent="center"
                  h="full"
                  className="gap-4"
                >
                  {logoFooterLeftUrl ? (
                    <Image
                      src={logoFooterLeftUrl}
                      h="3rem"
                      w="auto"
                      objectFit="contain"
                    />
                  ) : (
                    <Text fontSize="sm" color="white">
                      Logo Footer Kiri
                    </Text>
                  )}
                  <Box className="border-r border-white h-8" />
                  {logoFooterCenterUrl ? (
                    <Image
                      w="auto"
                      h="3rem"
                      src={logoFooterCenterUrl}
                      objectFit="contain"
                    />
                  ) : (
                    <Text fontSize="sm" color="white">
                      Logo Footer Tengah
                    </Text>
                  )}
                </Flex>
              </Box>
              <Box mt={4} display="flex" justifyContent={{ md: 'flex-end' }}>
                <Text fontSize="xl" fontWeight="semibold" color="white">
                  "Belajar Mandiri, Pintar Bersama"
                </Text>
              </Box>
            </Box>
            <Divider my={4} />
            <Box
              flexDirection={{ base: 'column', md: 'row' }}
              display={{ base: 'none', md: 'flex' }}
              justifyContent="space-between"
              alignItems="center"
            >
              <Box
                display="flex"
                flexDirection="row"
                className="gap-4"
                alignItems="center"
              >
                <Box display="flex" flexDirection="row" className="gap-2">
                  <PiPhoneFill size={14} color="white" className="mt-1" />
                  <Box display="flex" flexDirection="column">
                    <Text color="white">Hubungi Kami</Text>
                    <Text fontSize="lg" fontWeight="medium" color="white">
                      {contact}
                    </Text>
                  </Box>
                </Box>
                <Box className="border-r border-white h-8" />
                <Flex>
                  <Link mr={2} href={urlFacebook} isExternal>
                    <FaSquareFacebook color="white" size={20} />
                  </Link>
                  <Link mr={2} href={urlInstagram} isExternal>
                    <FaInstagramSquare color="white" size={20} />
                  </Link>
                  <Link mr={2} href={urlYoutube} isExternal>
                    <FaYoutubeSquare color="white" size={20} />
                  </Link>
                  <Link mr={2} href={urlTwitter} isExternal>
                    <FaTwitterSquare color="white" size={20} />
                  </Link>
                </Flex>
              </Box>
              <Flex direction="column">
                <Flex
                  direction="row"
                  className="gap-4 text-white mb-4 justify-end"
                >
                  <Link
                    href="/privacy-policy"
                    className="border-r border-white pr-4"
                  >
                    <p className="underline">Privacy &amp; Policy</p>
                  </Link>
                  <Link href="/request-delete-account">
                    <p className="underline">Request Delete Account</p>
                  </Link>
                </Flex>
                <Text color="white" fontSize={{ base: '11px', md: 'sm' }}>
                  © {copyright}
                </Text>
              </Flex>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <SidebarWithHeader spacing={false}>
      <Box
        p={4}
        bg="#EDF2F7"
        boxShadow="0px 10px 15px -3px rgba(15, 23, 42, 0.08)"
      >
        <Flex justifyContent="space-between" alignItems="center">
          <Flex justifyContent="center" alignItems="center">
            <Heading
              as="h2"
              fontSize="md"
              fontWeight="bold"
              lineHeight="normal"
            >
              Manajemen Identitas
            </Heading>
          </Flex>
          <Flex alignItems="center" justifyContent="center">
            <Switch
              defaultChecked={isPreview}
              colorScheme="green"
              mr={2}
              mt={1}
              onChange={(e) => setState('isPreview', !isPreview)}
            />
            <Text fontWeight="semibold" fontSize="sm">
              Preview
            </Text>
            {canMutate && (
              <Button
                isLoading={loadingAction}
                colorScheme="green"
                ml={3}
                onClick={() => {
                  postData();
                }}
              >
                Simpan
              </Button>
            )}
          </Flex>
        </Flex>
      </Box>

      <Box m={4} px={4} py={8} boxShadow="lg" borderRadius="xl" bg="white">
        {loadingData ? (
          <Loader />
        ) : isPreview ? (
          <PreviewComponent />
        ) : (
          <Grid templateColumns="repeat(6, 1fr)" gap={4}>
            {/* Logo Navbar */}
            <GridItem colSpan={1}>
              <Text fontWeight="semibold" fontSize="sm">
                Logo Navbar
              </Text>
            </GridItem>
            <GridItem colSpan={5}>
              <FileUpload
                onChange={setLogoNavbar}
                defaultImage={logoNavbarUrl}
              />
            </GridItem>

            {/* Logo Footer Kiri */}
            <GridItem colSpan={1}>
              <Text fontWeight="semibold" fontSize="sm">
                Logo Footer Kiri
              </Text>
            </GridItem>
            <GridItem colSpan={5}>
              <FileUpload
                onChange={setLogoFooterLeft}
                defaultImage={logoFooterLeftUrl}
              />
            </GridItem>

            {/* Logo Footer Tengah */}
            <GridItem colSpan={1}>
              <Text fontWeight="semibold" fontSize="sm">
                Logo Footer Tengah
              </Text>
            </GridItem>
            <GridItem colSpan={5}>
              <FileUpload
                onChange={setLogoFooterCenter}
                defaultImage={logoFooterCenterUrl}
              />
            </GridItem>

            {/* Link */}
            <GridItem colSpan={1}>
              <Text fontWeight="semibold" fontSize="sm">
                Kontak
              </Text>
            </GridItem>
            <GridItem colSpan={5}>
              <Input
                defaultValue={contact}
                bg="#F6F5F8"
                _focus={{
                  ring: '1',
                  ringColor: 'green',
                  borderColor: 'green',
                }}
                onChange={(e) => setState('contact', e.target.value)}
              />
            </GridItem>

            {/* Link */}
            <GridItem colSpan={1}>
              <Text fontWeight="semibold" fontSize="sm">
                Url Facebook
              </Text>
            </GridItem>
            <GridItem colSpan={5}>
              <Input
                defaultValue={urlFacebook}
                bg="#F6F5F8"
                _focus={{
                  ring: '1',
                  ringColor: 'green',
                  borderColor: 'green',
                }}
                onChange={(e) => setState('urlFacebook', e.target.value)}
              />
            </GridItem>

            {/* Link */}
            <GridItem colSpan={1}>
              <Text fontWeight="semibold" fontSize="sm">
                Url Instagram
              </Text>
            </GridItem>
            <GridItem colSpan={5}>
              <Input
                defaultValue={urlInstagram}
                bg="#F6F5F8"
                _focus={{
                  ring: '1',
                  ringColor: 'green',
                  borderColor: 'green',
                }}
                onChange={(e) => setState('urlInstagram', e.target.value)}
              />
            </GridItem>

            {/* Link */}
            <GridItem colSpan={1}>
              <Text fontWeight="semibold" fontSize="sm">
                Url Youtube
              </Text>
            </GridItem>
            <GridItem colSpan={5}>
              <Input
                defaultValue={urlYoutube}
                bg="#F6F5F8"
                _focus={{
                  ring: '1',
                  ringColor: 'green',
                  borderColor: 'green',
                }}
                onChange={(e) => setState('urlYoutube', e.target.value)}
              />
            </GridItem>

            {/* Link */}
            <GridItem colSpan={1}>
              <Text fontWeight="semibold" fontSize="sm">
                Url Twitter
              </Text>
            </GridItem>
            <GridItem colSpan={5}>
              <Input
                defaultValue={urlTwitter}
                bg="#F6F5F8"
                _focus={{
                  ring: '1',
                  ringColor: 'green',
                  borderColor: 'green',
                }}
                onChange={(e) => setState('urlTwitter', e.target.value)}
              />
            </GridItem>

            {/* Link */}
            <GridItem colSpan={1}>
              <Text fontWeight="semibold" fontSize="sm">
                Copyright
              </Text>
            </GridItem>
            <GridItem colSpan={5}>
              <Input
                defaultValue={copyright}
                bg="#F6F5F8"
                _focus={{
                  ring: '1',
                  ringColor: 'green',
                  borderColor: 'green',
                }}
                onChange={(e) => setState('copyright', e.target.value)}
              />
            </GridItem>
          </Grid>
        )}
      </Box>
    </SidebarWithHeader>
  );
};

const ManajemenIdentitasMain: React.FC = () => {
  return (
    <ManajemenIdentitasController.Provider>
      <ManajemenIdentitas />
    </ManajemenIdentitasController.Provider>
  );
};

export default ManajemenIdentitasMain;
